import React, {useState, useEffect, useReducer, useCallback} from 'react';
import {useSeriesStore} from "../../store/useSeriesStore.ts";
import AppSpinner from "../AppSpinner.tsx";
import SeriesModalOverview from "./SeiresModalOverview.tsx";
import SeriesModalMenu from "../ModalSideMenu.tsx";
import {GenericMenuItem} from "../../models/interfaces/genericMenuItem.ts";
import {seriesModalItems} from "../../models/constants/seriesModalNevigationItems.ts";
import SeriesModalGeneralTab from "./SeriesModalGeneralTab.tsx";
import SeriesModalCharactersTab from "./SeriesModalCharactersTab.tsx";
import SeriesModalGenresTab from "./SeriesModalGenresTab.tsx";
import SeriesModalDictionaryTab from "./SeriesModalDictionaryTab.tsx";
import {initialSeriesModalState, seriesModalReducer} from "../../reducers/seriesModalReducer.ts";
import SeriesExternalFiles from "./SeriesExternalFiles.tsx";
import useUiStore from "../../store/useUiStore.ts";
import {useModalBeforeClose} from "../../hooks/useModalBeforeClose.ts";
import {comparingObjects} from "../../utils/utils.ts";

interface SeriesModalProps {
  seriesId: string;
}

const items: GenericMenuItem[] = seriesModalItems;

const SeriesModal: React.FC<SeriesModalProps> = ({seriesId}: SeriesModalProps) => {
  const {closeModal, showToast} = useUiStore()
  const {series, getSeries, updateSeries} = useSeriesStore();
  const [state, dispatch] = useReducer(seriesModalReducer, initialSeriesModalState);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUpdateSeries, setIsLoadingUpdateSeries] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<GenericMenuItem>(items[0]);

  const shouldBlockClose = useCallback(async () => {
    const hasUnsavedChanges = !comparingObjects(state.series?.info, series[seriesId].info);
    if (hasUnsavedChanges) {
      return window.confirm("You have unsaved changes. Close without saving?");
    }
    return true;
  }, [state.series, series, seriesId]);

  useModalBeforeClose(shouldBlockClose);

  const saveSeriesChanges = async () => {
    if (!state.series) return;
    try {
      setIsLoadingUpdateSeries(true);
      await updateSeries(state.series);
      setError(null);
      await closeModal();
      showToast("Changes saved successfully", "success");
    } catch (error) {
      console.log("Error saving series changes:", error);
      setError("Failed to save changes");
    } finally {
      setIsLoadingUpdateSeries(false);
    }

  }

  useEffect(() => {
    (async () => {
      if (!series[seriesId]) {
        setIsLoading(true);
        try {
          await getSeries(seriesId);
        } catch (error) {
          setError("Failed to fetch series");
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [seriesId, series, getSeries]);

  useEffect(() => {
    if (series[seriesId]) {
      dispatch({type: "INIT_SERIES", payload: series[seriesId]});
      setActiveItem(items[0]);
    }
  }, [series, seriesId]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <AppSpinner/>
      </div>
    );
  }

  if (error || !series[seriesId]) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-12 lg:px-8 md:pt-16 md:px-5 md:pb-8">
        <p className="text-red-500">{error || "Series not found"}</p>
      </div>
    );
  }
  return (
    <div className="p-12 lg:px-8 md:pt-16 md:px-5 md:pb-8">
      <div className="flex flex-col w-full h-full overflow-hidden">
        <SeriesModalOverview series={series[seriesId]}/>
      </div>
      <div className="flex md:block mt-6">
        <div className="shrink-0 w-[13.25rem]">
          <SeriesModalMenu items={seriesModalItems} activeItem={activeItem} setActiveItem={setActiveItem}/>
        </div>
        {state.series && (
          <div className="grow pl-12 md:pl-0">
            {activeItem.id === "general" && (
              <SeriesModalGeneralTab
                seriesInfo={state.series.info}
                dispatch={dispatch}
              />
            )}
            {activeItem.id === "characters" && (
              <SeriesModalCharactersTab
                seriesCharacters={state.series.info.characters}
                dispatch={dispatch}
              />
            )}
            {activeItem.id === "genres" && (
              <SeriesModalGenresTab
                genres={state.series.info.genres}
                dispatch={dispatch}
              />
            )
            }
            {activeItem.id === "dictionary" && (
              <SeriesModalDictionaryTab
                seriesName={state.series.name}
                terms={state.series.info.terms}
                dispatch={dispatch}
              />
            )
            }
            {activeItem.id === "externalFiles" && (
              <SeriesExternalFiles
                seriesId={seriesId}
                menuItems={items}
                setActiveItem={setActiveItem}
              />
            )
            }
          </div>
        )}
      </div>
      <div className="flex items-center justify-end mt-6">
        {activeItem.id !== "externalFiles" && (
          <button
            className="btn-purple min-w-[10rem]"
            onClick={saveSeriesChanges}
            disabled={isLoadingUpdateSeries}
          >
            {isLoadingUpdateSeries ? <AppSpinner size={"md"} color={"white"}/> : "Save Changes"}
          </button>
        )
        }

      </div>
    </div>
  );
};

export default SeriesModal;